import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useCommunityContext } from '../contexts/CommunityContext';
import { credentialCacheScope } from '../services/credentialCacheScope';
import { actorIdOf, captureQueryScope, communityIdOf, ownsQueryScope, queryScopeId } from './queryOwnership';

export const useQueryScope = (options = {}) => {
  // Contexts are read at render only: CommunityContext also uses this hook.
  const auth = useAuthContext() || {};
  const communityContext = useCommunityContext() || {};
  const hasProvider = auth.hasProvider === true;
  const isSessionCurrent = auth.isSessionCurrent;
  const user = options.user === undefined ? auth.user : options.user;
  const token = options.token === undefined ? auth.token : options.token;
  const communityId = Object.prototype.hasOwnProperty.call(options, 'communityId')
    ? options.communityId
    : communityIdOf(communityContext.community) || communityIdOf(user?.community ?? user?.communityId);
  const captured = captureQueryScope({ user, token, communityId });
  const standaloneCredential = useMemo(() => credentialCacheScope(token), [token]);
  const candidate = captured || (!hasProvider && actorIdOf(user) && token ? {
    actorId: actorIdOf(user), communityId: communityIdOf(communityId) || '',
    sessionId: `standalone-${standaloneCredential}`,
  } : null);
  const scopeId = queryScopeId(candidate);
  // Identity drives effects and setters downstream, so preserve reference
  // equality when an auth user object is merely enriched with other metadata.
  const scopeRef = useRef({ id: scopeId, scope: candidate });
  if (scopeRef.current.id !== scopeId) scopeRef.current = { id: scopeId, scope: candidate };
  const scope = scopeRef.current.scope;
  const current = useRef({ scopeId, mounted: true });
  current.current.scopeId = scopeId;
  useEffect(() => {
    const state = current.current;
    state.mounted = true;
    return () => { state.mounted = false; };
  }, []);
  const isCurrent = useCallback(() => current.current.mounted
    && current.current.scopeId === scopeId
    && (!hasProvider || isSessionCurrent?.())
    && (!hasProvider || ownsQueryScope(scope)), [isSessionCurrent, hasProvider, scopeId, scope]);
  return { scope, scopeId, isCurrent, enabled: hasProvider ? Boolean(scope) : true, hasProvider };
};
export default useQueryScope;
